// @djsem/render-state — shared mapping from a conductor DJSEMOutputFrame to a renderer-
// agnostic preview model (LED wall / lasers / semantic projectors + intensity budget).
// This is the forward-looking shared boundary consumed by the Electron live renderer and,
// incrementally, the POC. The POC's existing in-app `engine/` render code remains in place
// during migration (see docs/APP_BOUNDARIES.md).
import type { DJSEMOutputFrame } from "./outputFrame";
export * from "./outputFrame";

/** Renderer-neutral preview state for a single simulated output layer (LED wall/lasers/projectors). */
export interface RenderLayerPreview { mode: string; intensity: number; palette?: string[]; emoji?: string[]; safe: boolean; }
/** Renderer-agnostic preview model derived from a conductor output frame, for the Electron live renderer / POC. */
export interface LivePerformanceRenderState {
  cueId?: string; packageId?: string;
  ledWall: RenderLayerPreview; lasers: RenderLayerPreview; projectors: RenderLayerPreview;
  intensityBudget: { total: number; dominantLayer: string }; safeModeActive: boolean;
}

/**
 * Turns a conductor output frame into a renderer-agnostic preview render state. Pure
 * function; safe-mode frames are passed through with their clamped (safe) values intact.
 * @param frame - the output frame produced by the live conductor/simulator.
 * @returns the derived preview render state.
 */
export function buildLivePerformanceRenderState(frame: DJSEMOutputFrame): LivePerformanceRenderState {
  return {
    cueId: frame.cueId, packageId: frame.packageId,
    ledWall: { mode: frame.ledWall.mode, intensity: frame.ledWall.intensity, palette: frame.ledWall.palette, safe: frame.ledWall.safe },
    lasers: { mode: frame.lasers.mode, intensity: frame.lasers.intensity, safe: frame.lasers.safe },
    projectors: { mode: frame.projectors.mode, intensity: frame.projectors.intensity, emoji: frame.projectors.emojiSequence, safe: frame.projectors.safe },
    intensityBudget: { total: frame.intensityBudget.total, dominantLayer: frame.intensityBudget.dominantLayer },
    safeModeActive: frame.safety.safeModeActive,
  };
}
export * from "./scenes";
export * from "./brandWorld";
export * from "./visualSceneEngine";
export * from "./lightingSceneEngine";
export * from "./proAdapters";
export * from "./venueProfile";
export * from "./releaseStudio";
export * from "./visualBuilder";
export * from "./contentGeneration";
export * from "./mappingEditor";
export * from "./venueRightsSafety";
